import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { fetchMe } from '@/api/auth';
import { fetchClients } from '@/api/clients';
import { useAuthGate } from '@/hooks/useAuthGate';
import type { Session } from '@/context/SessionContext';
import { AppLayout } from './AppLayout';
import { SessionProvider } from './SessionProvider';
import { Spinner } from './Spinner';

/**
 * Проверка входа на уровне всего приложения (одна на все защищённые
 * маршруты). Без сессии – редирект на /login: отдельный маршрут вне
 * AppLayout, без сайдбара/топбара – не «дашборд с заглушкой внутри».
 * С сессией – обычный каркас (AppLayout) со всеми вложенными страницами;
 * пользователь грузится один раз здесь и раздаётся дальше через
 * SessionProvider – RootGate не перемонтируется при переходах между
 * страницами, так что повторных запросов /auth/me на каждой странице не будет.
 *
 * Портфель (счётчик в сайдбаре) — отдельный, независимый от /auth/me запрос:
 * best-effort довесок (если /clients по какой-то причине не ответил, это не
 * повод разлогинивать, просто плашка не покажется, см. PortfolioChip), и, в
 * отличие от пользователя, МОЖЕТ устареть на других страницах (создание/
 * удаление клиента) — поэтому даётся как функция refreshPortfolioSize()
 * через Session, а не только как значение на момент гейта.
 */
export function RootGate() {
  const authState = useAuthGate(fetchMe);
  const [portfolioSize, setPortfolioSize] = useState<number | null>(null);

  const refreshPortfolioSize = useCallback(() => {
    fetchClients({ page: 1 })
      .then((page) => setPortfolioSize(page.meta.total))
      .catch(() => setPortfolioSize(null));
  }, []);

  useEffect(() => {
    if (authState.kind === 'authed') refreshPortfolioSize();
  }, [authState.kind, refreshPortfolioSize]);

  if (authState.kind === 'loading') {
    return (
      <section className="gate">
        <Spinner />
      </section>
    );
  }

  // На входе в приложение любой отказ — это «нет сессии»: без /auth/me показывать
  // нечего, и единственный осмысленный шаг — вход. Внутри уже загруженного
  // приложения 403 разбирается иначе, см. AuthGate.
  if (authState.kind === 'anon' || authState.kind === 'error') {
    return <Navigate to="/login" replace />;
  }

  const session: Session = { user: authState.data, portfolioSize, refreshPortfolioSize };

  return (
    <SessionProvider value={session}>
      <AppLayout />
    </SessionProvider>
  );
}
